import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

/**
 * SSOConfigManager - Full CRUD for HealthcareUserSSOConfig
 * Surfaces ALL fields including complex JSON fields for security, tracking, and compliance
 */
const SSOConfigManager = ({ userId, onUpdate }) => {
  const [configs, setConfigs] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeSection, setActiveSection] = useState('basic'); // basic, identifiers, security, login, compliance, session
  const [formData, setFormData] = useState({
    membership_id: '',
    sso_provider_type: '',
    sso_identifiers: {},
    sso_security: {},
    login_tracking: {},
    compliance_tracking: {},
    session_management: {},
    sso_metadata: {}
  });

  useEffect(() => {
    fetchData();
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configsRes, membershipsRes] = await Promise.all([
        adminApi.getUserSsoConfigs(userId),
        adminApi.getUserMemberships(userId)
      ]);

      setConfigs(configsRes.data?.sso_configs || []);
      setMemberships(membershipsRes.data?.memberships || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load SSO configs');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingId(null);
    setActiveSection('basic');
    setFormData({
      membership_id: '',
      sso_provider_type: '',
      sso_identifiers: {},
      sso_security: {},
      login_tracking: {},
      compliance_tracking: {},
      session_management: {},
      sso_metadata: {}
    });
  };

  const handleEdit = (config) => {
    setEditingId(config.id);
    setShowAddForm(true);
    setActiveSection('basic');
    setFormData({
      membership_id: config.membership?.id || '',
      sso_provider_type: config.sso_provider_type || '',
      sso_identifiers: config.sso_identifiers || {},
      sso_security: config.sso_security || {},
      login_tracking: config.login_tracking || {},
      compliance_tracking: config.compliance_tracking || {},
      session_management: config.session_management || {},
      sso_metadata: config.sso_metadata || {}
    });
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await adminApi.updateUserSsoConfig(userId, editingId, formData);
      } else {
        await adminApi.createUserSsoConfig(userId, formData);
      }
      setShowAddForm(false);
      setEditingId(null);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save SSO config');
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this SSO config?')) return;

    try {
      await adminApi.deleteUserSsoConfig(userId, configId);
      fetchData();
      if (onUpdate) onUpdate();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete SSO config');
    }
  };

  const updateNestedField = (parent, key, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header py-2 bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="fas fa-lock me-2" />
          SSO Configurations
        </h6>
        {!showAddForm && (
          <button className="btn btn-sm btn-primary" onClick={handleAdd}>
            <i className="fas fa-plus me-1" />Add SSO Config
          </button>
        )}
      </div>

      <div className="card-body p-2">
        {error && <div className="alert alert-danger small mb-2">{error}</div>}

        {showAddForm && (
          <div className="border rounded p-3 mb-3 bg-light">
            <h6 className="mb-3">{editingId ? 'Edit SSO Config' : 'Add SSO Config'}</h6>

            {/* Section Tabs */}
            <ul className="nav nav-pills nav-sm mb-3">
              <li className="nav-item">
                <button className={`nav-link ${activeSection === 'basic' ? 'active' : ''}`} onClick={() => setActiveSection('basic')}>
                  Basic
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeSection === 'identifiers' ? 'active' : ''}`} onClick={() => setActiveSection('identifiers')}>
                  Identifiers
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeSection === 'security' ? 'active' : ''}`} onClick={() => setActiveSection('security')}>
                  Security
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeSection === 'login' ? 'active' : ''}`} onClick={() => setActiveSection('login')}>
                  Login Tracking
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeSection === 'compliance' ? 'active' : ''}`} onClick={() => setActiveSection('compliance')}>
                  Compliance
                </button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeSection === 'session' ? 'active' : ''}`} onClick={() => setActiveSection('session')}>
                  Sessions
                </button>
              </li>
            </ul>

            {/* Basic Section */}
            {activeSection === 'basic' && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label small mb-1">Membership</label>
                  <select
                    className="form-select form-select-sm"
                    value={formData.membership_id}
                    onChange={e => setFormData({ ...formData, membership_id: e.target.value })}
                  >
                    <option value="">No Membership Link</option>
                    {memberships.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.health_system?.name} - {m.role?.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label small mb-1">SSO Provider Type *</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={formData.sso_provider_type}
                    onChange={e => setFormData({ ...formData, sso_provider_type: e.target.value })}
                    placeholder="Microsoft, Google, Okta, Epic, Azure AD, etc."
                    required
                  />
                </div>
              </div>
            )}

            {/* Identifiers Section */}
            {activeSection === 'identifiers' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">SSO Identifiers (state, nonce, subject ID, provider info, entity IDs)</small>
                </div>
                {['state', 'nonce', 'sso_subject_id', 'sso_provider', 'idp_entity_id', 'sp_entity_id', 'saml_name_id'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.sso_identifiers[key] || ''}
                      onChange={e => updateNestedField('sso_identifiers', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Security Settings (MFA, sessions, passwords, IP whitelisting)</small>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.sso_security.mfa_enabled || false}
                      onChange={e => updateNestedField('sso_security', 'mfa_enabled', e.target.checked)}
                    />
                    <label className="form-check-label small">MFA Enabled</label>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.sso_security.require_password_change || false}
                      onChange={e => updateNestedField('sso_security', 'require_password_change', e.target.checked)}
                    />
                    <label className="form-check-label small">Require Password Change</label>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.sso_security.require_yubikey || false}
                      onChange={e => updateNestedField('sso_security', 'require_yubikey', e.target.checked)}
                    />
                    <label className="form-check-label small">Require YubiKey</label>
                  </div>
                </div>
                {['mfa_methods', 'session_timeout_minutes', 'max_concurrent_sessions', 'ip_whitelist', 'allowed_devices'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.sso_security[key] || ''}
                      onChange={e => updateNestedField('sso_security', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Login Tracking Section */}
            {activeSection === 'login' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Login Activity Tracking</small>
                </div>
                {['login_count', 'failed_attempts', 'last_failed_login', 'last_password_change', 'account_locked_until', 'lockout_reason', 'recent_login_ips'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.login_tracking[key] || ''}
                      onChange={e => updateNestedField('login_tracking', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Compliance Section */}
            {activeSection === 'compliance' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Compliance and Credentialing</small>
                </div>
                {['hipaa_training_date', 'hipaa_training_expiry', 'background_check_date', 'credential_verification_date', 'cme_credits', 'license_verification_date'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type={key.includes('date') ? 'date' : 'text'}
                      className="form-control form-control-sm"
                      value={formData.compliance_tracking[key] || ''}
                      onChange={e => updateNestedField('compliance_tracking', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Session Management Section */}
            {activeSection === 'session' && (
              <div className="row">
                <div className="col-12 mb-2">
                  <small className="text-muted">Session Management and Policies</small>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={formData.session_management.auto_logout_enabled || false}
                      onChange={e => updateNestedField('session_management', 'auto_logout_enabled', e.target.checked)}
                    />
                    <label className="form-check-label small">Auto Logout Enabled</label>
                  </div>
                </div>
                {['active_sessions', 'device_fingerprints', 'session_policies', 'inactivity_timeout'].map(key => (
                  <div key={key} className="col-md-6 mb-3">
                    <label className="form-label small mb-1">{key.replace(/_/g, ' ').toUpperCase()}</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={formData.session_management[key] || ''}
                      onChange={e => updateNestedField('session_management', key, e.target.value)}
                      placeholder={`Enter ${key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="btn-group btn-group-sm mt-3">
              <button className="btn btn-success" onClick={handleSave}>
                <i className="fas fa-check me-1" />
                {editingId ? 'Update' : 'Create'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                <i className="fas fa-times me-1" />Cancel
              </button>
            </div>
          </div>
        )}

        {configs.length === 0 ? (
          <div className="text-center text-muted small py-3">
            <i className="fas fa-lock me-1" />
            No SSO configurations
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Provider</th>
                  <th>Membership</th>
                  <th>MFA</th>
                  <th>Last Login</th>
                  <th>Compliance</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.sso_provider_type}</strong></td>
                    <td>
                      {c.membership?.health_system?.name || '-'}
                      <div className="small text-muted">{c.membership?.role?.name}</div>
                    </td>
                    <td>
                      <span className={`badge bg-${c.sso_security?.mfa_enabled ? 'success' : 'secondary'}`}>
                        {c.sso_security?.mfa_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="small">
                      {c.last_sso_login ? new Date(c.last_sso_login).toLocaleString() : 'Never'}
                    </td>
                    <td className="small">
                      {c.compliance_tracking?.hipaa_training_expiry ? (
                        <div>HIPAA: {new Date(c.compliance_tracking.hipaa_training_expiry).toLocaleDateString()}</div>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleEdit(c)}
                          title="Edit"
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(c.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SSOConfigManager;
